import React, { useState, useEffect, useMemo } from 'react';
import { socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
import MedalPage from './MedalPage';
import PodiumPage from './PodiumPage';
import SocketNotification from '../components/SocketNotification';
import './PodiumSlideshowPage.css';

interface PodiumConfig {
  title: string;
  subtitle: string;
  teams: {
    first?: string;
    second?: string;
    third?: string;
  };
  teamImages: {
    [teamName: string]: string;
  };
  showPodium: boolean;
  currentSlide: number;
  // Agregar configuraciones de otras secciones para acceso a equipos
  leaderboard?: any;
  bracket?: any;
}

const PodiumSlideshowPage: React.FC = () => {
  const [config, setConfig] = useState<PodiumConfig>({
    title: 'Podium de Ganadores',
    subtitle: 'Neurolimpiadas 2025',
    teams: {},
    teamImages: {},
    showPodium: false,
    currentSlide: 0
  });
  
  // Estado para controlar la visibilidad de la imagen intercalada
  const [intercaladaVisible, setIntercaladaVisible] = useState(false);
  
  // Flag para evitar ejecutar la secuencia automática múltiples veces
  const [autoSequenceExecuted, setAutoSequenceExecuted] = useState(false);
  
  // Estado para controlar si se está en transición (bloquea interacciones)
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Definir las diapositivas con intercaladas
  const slides = useMemo(() => [
    { id: 0, name: 'Imagen Intercalada', component: 'intercalada' },
    { id: 1, name: 'Medalla de Bronce', component: 'bronze' },
    { id: 2, name: 'Imagen Intercalada', component: 'intercalada' },
    { id: 3, name: 'Medalla de Plata', component: 'silver' },
    { id: 4, name: 'Imagen Intercalada', component: 'intercalada' },
    { id: 5, name: 'Medalla de Oro', component: 'gold' },
    { id: 6, name: 'Podium Completo', component: 'podium' }
  ], []);

  // Cargar configuración inicial
  useEffect(() => {
    const savedConfig = localStorageManager.getConfig();
    if (savedConfig.podium) {      setConfig(prev => ({
        ...prev,
        ...savedConfig.podium,
        currentSlide: (savedConfig.podium as any).currentSlide || 0,
        // Cargar también las configuraciones de equipos desde otras secciones
        leaderboard: savedConfig.leaderboard,
        bracket: savedConfig.bracket
      }));
      console.log('🏆 PodiumSlideshowPage - Configuración inicial cargada:', {
        podium: savedConfig.podium,
        equiposLeaderboard: savedConfig.leaderboard?.teams?.length || 0,
        equiposBracket: savedConfig.bracket?.teams?.length || 0
      });
    }
  }, []);

  // Event listeners
  useEffect(() => {
    const handleConfigUpdate = (data: any) => {
      if (data.section === 'podium') {
        setConfig(prev => ({ ...prev, ...data.data }));
      }
      // También actualizar cuando cambien leaderboard o bracket (para equipos disponibles)
      if (data.section === 'leaderboard') {
        setConfig(prev => ({ ...prev, leaderboard: data.data }));
        console.log('🎬 PodiumSlideshowPage - Equipos de leaderboard actualizados:', data.data.teams?.length || 0);
      }
      if (data.section === 'bracket') {
        setConfig(prev => ({ ...prev, bracket: data.data }));
        console.log('🎬 PodiumSlideshowPage - Equipos de bracket actualizados:', data.data.teams?.length || 0);
      }
    };

    const handlePodiumCommand = (command: any) => {
      console.log('🎬 PodiumSlideshowPage - Comando recibido:', command);
      const { command: cmd, payload } = command;
      
      // Bloquear comandos de navegación si está en transición
      if (isTransitioning && (cmd === 'nextSlide' || cmd === 'prevSlide' || cmd === 'goToSlide')) {
        console.log('⏳ Comando bloqueado: transición en progreso');
        return;
      }
      
      switch (cmd) {
        case 'showPodium':
          setConfig(prev => ({ ...prev, showPodium: true }));
          break;
        case 'hidePodium':
          setConfig(prev => ({ ...prev, showPodium: false }));
          break;
        case 'nextSlide':
          setIsTransitioning(true);
          setConfig(prev => ({
            ...prev,
            currentSlide: Math.min(prev.currentSlide + 1, slides.length - 1)
          }));
          // Desbloquear después de la duración de transición
          setTimeout(() => setIsTransitioning(false), 1000);
          break;
        case 'prevSlide':
          setIsTransitioning(true);
          setConfig(prev => ({
            ...prev,
            currentSlide: Math.max(prev.currentSlide - 1, 0)
          }));
          // Desbloquear después de la duración de transición
          setTimeout(() => setIsTransitioning(false), 1000);
          break;
        case 'goToSlide':
          if (payload && typeof payload.slideIndex === 'number') {
            setIsTransitioning(true);
            setConfig(prev => ({
              ...prev,
              currentSlide: Math.max(0, Math.min(payload.slideIndex, slides.length - 1))
            }));
            // Desbloquear después de la duración de transición
            setTimeout(() => setIsTransitioning(false), 1000);
          }
          break;        case 'reset':
          setConfig(prev => ({
            ...prev,
            teams: {},
            showPodium: false,
            currentSlide: 0
          }));
          setIsTransitioning(false);
          break;
        // Comandos manuales para el podium completo - solo procesar si NO es el comando automático
        case 'showThird':
        case 'hideThird':
        case 'showSecond':
        case 'hideSecond':
        case 'showFirst':
        case 'hideFirst':
          // Ignorar estos comandos en PodiumSlideshowPage para evitar duplicación
          // El PodiumPage los manejará directamente
          console.log(`🎯 Comando ${cmd} procesado por PodiumPage`);
          break;
        default:
          console.log('❓ Comando no reconocido:', cmd);
      }
    };

    socketService.on('configUpdate', handleConfigUpdate);
    socketService.on('podiumCommand', handlePodiumCommand);

    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
      socketService.off('podiumCommand', handlePodiumCommand);
    };  }, [slides.length, config.currentSlide, isTransitioning]);

  // Efecto para manejar la visibilidad de la imagen intercalada
  useEffect(() => {
    const currentSlideInfo = slides[config.currentSlide];
    if (currentSlideInfo?.component === 'intercalada') {
      // Pequeño delay para suavizar la transición
      const timer = setTimeout(() => {
        setIntercaladaVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIntercaladaVisible(false);
    }
  }, [config.currentSlide, slides]);

  // Efecto para simular automáticamente la secuencia del podium
  useEffect(() => {
    // Solo ejecutar cuando estemos en la diapositiva del podium (índice 6), showPodium sea true
    // y la secuencia no se haya ejecutado aún
    if (config.currentSlide === 6 && config.showPodium && !autoSequenceExecuted) {
      console.log('🎬 Iniciando secuencia automática del podium...');
      
      // Marcar como ejecutada para evitar repeticiones
      setAutoSequenceExecuted(true);
      
      // Bloquear interacciones durante la secuencia automática
      setIsTransitioning(true);
      
      // Limpiar cualquier timeout previo
      const timeouts: NodeJS.Timeout[] = [];
      
      // Secuencia automática progresiva (cada posición individualmente):
      // 1. Mostrar 3º lugar después de 500ms
      timeouts.push(setTimeout(() => {
        console.log('🥉 Mostrando automáticamente el tercer lugar...');
        socketService.emit('podiumCommand', { command: 'showThird' });
      }, 500));
      
      // 2. Ocultar 3º y mostrar 2º lugar después de 1000ms
      timeouts.push(setTimeout(() => {
        console.log('🥈 Mostrando automáticamente el segundo lugar...');
        socketService.emit('podiumCommand', { command: 'hideThird' });
        setTimeout(() => {
          socketService.emit('podiumCommand', { command: 'showSecond' });
        }, 100);
      }, 1000));
      
      // 3. Ocultar 2º y mostrar 1º lugar después de 1500ms
      timeouts.push(setTimeout(() => {
        console.log('🥇 Mostrando automáticamente el primer lugar...');
        socketService.emit('podiumCommand', { command: 'hideSecond' });
        setTimeout(() => {
          socketService.emit('podiumCommand', { command: 'showFirst' });
        }, 100);
      }, 1500));
      
      // 4. Mostrar todos al final después de 2000ms
      timeouts.push(setTimeout(() => {
        console.log('🏆 Mostrando podium completo...');
        socketService.emit('podiumCommand', { command: 'showThird' });
        setTimeout(() => socketService.emit('podiumCommand', { command: 'showSecond' }), 100);
        setTimeout(() => socketService.emit('podiumCommand', { command: 'showFirst' }), 200);
        
        // Desbloquear interacciones después de mostrar todo
        setTimeout(() => setIsTransitioning(false), 500);
      }, 2000));
      
      // Cleanup function para limpiar timeouts si el componente se desmonta o cambia de diapositiva
      return () => {
        timeouts.forEach(timeout => clearTimeout(timeout));
        setIsTransitioning(false);
      };
    }
    
    // Reset del flag cuando se cambia de diapositiva
    if (config.currentSlide !== 6) {
      setAutoSequenceExecuted(false);
    }
  }, [config.currentSlide, config.showPodium, autoSequenceExecuted]);

  // Renderizar la diapositiva actual
  const renderCurrentSlide = () => {
    const currentSlideInfo = slides[config.currentSlide];
    
    if (!currentSlideInfo) {
      return <div>Error: Diapositiva no encontrada</div>;
    }

    switch (currentSlideInfo.component) {      case 'intercalada':
        return (
          <div className={`intercalada-container ${intercaladaVisible ? 'visible' : ''}`}>
            <img src="/img/neurolimpiadas_back.svg" alt="Neuroteide" className="intercalada-image" />
          </div>
        );
      case 'bronze':
        return <MedalPage position="bronze" />;
      case 'silver':
        return <MedalPage position="silver" />;
      case 'gold':
        return <MedalPage position="gold" />;
      case 'podium':
        return <PodiumPage />;
      default:
        return <div>Error: Componente no reconocido</div>;
    }
  };

  // Si no se debe mostrar el podium, mostrar pantalla de espera
  if (!config.showPodium) {
    return (
      <div className="podium-slideshow-waiting">
        <SocketNotification showOnMount={true} />
        <div className="waiting-content">
          <div className="waiting-icon">🏆</div>
          <h2>Preparando ceremonia de premiación...</h2>
          <p>La ceremonia comenzará pronto</p>
        </div>
        
      </div>
    );
  }  return (
    <div className="podium-slideshow-container">

      {/* Renderizar diapositiva actual */}
      <div className="slide-content">
        {renderCurrentSlide()}
      </div>

      {/* Controles de navegación (solo para desarrollo/debug) */}
{/*       {process.env.NODE_ENV === 'development' && (
        <div className="slide-controls-debug">
          <button onClick={() => socketService.emit('podiumCommand', { command: 'prevSlide' })}>
            ← Prev
          </button>
          <button onClick={() => socketService.emit('podiumCommand', { command: 'nextSlide' })}>
            Next →
          </button>
        </div>
      )} */}
    </div>
  );
};

export default PodiumSlideshowPage;
